import { boolean, id, model, string } from "@moirei/dobby";
import type { Dayjs } from "dayjs";
import { omitBy, isNil } from "lodash";
import {
  type Publishable,
  Model,
  datetime,
  PublishableStatus,
  type PublishedStatus,
  OnboardStep,
  savesInContext,
} from "@/layers/admin/models";
import { Media } from "../../layers/admin/models/dobby/Media";
import ResourceFaq from "./ResourceFaq";
import { OrderForm } from "./OrderForm";

/**
 * Base class for publishable resources (Product, Service, Event)
 * Implements common properties and methods for resources that can be published
 */
export abstract class PublishableResource extends Model implements Publishable {
  constructor(attributes = {}) {
    super({
      gallery: [],
      tags: [],
      faqs: [],
      order_form: {},
      ...omitBy(attributes, isNil),
    });
  }

  // Core identifiers
  @datetime() declare readonly created_at: Dayjs;
  @datetime() declare readonly updated_at: Dayjs;
  @id() declare readonly id: string;
  @id() declare readonly gid: string;
  @string() declare readonly handle: string;
  @string() declare readonly objectName: string;

  // Basic information
  @string() declare name: string;
  @string() declare description?: string;
  @string() declare currency_code?: string;

  // Publishing status
  @string() declare status: PublishableStatus;
  @string() declare publishedStatus: PublishedStatus;
  @boolean() declare hasUnpublishedChanges: boolean;

  // Privacy and visibility
  @boolean() declare private?: boolean;
  @string({ default: [], list: true }) declare tags: string[];

  // Media
  @model(() => Media) declare image?: Media;
  @model(() => Media, { list: true }) declare gallery?: Media[];
  @string() declare video_url?: string;

  // Additional settings
  @string() declare purchase_action?: string;

  // Relationships
  @model(() => ResourceFaq, { list: true }) declare faqs: ResourceFaq[];
  @model(() => OnboardStep, { list: true }) declare nextSteps?: OnboardStep[];
  @model(() => OrderForm)
  @savesInContext()
  declare order_form?: OrderForm;

  // Common getters
  get lastUpdated(): Dayjs {
    return this.updated_at || this.created_at;
  }

  // Common methods
  $isPublished(): boolean {
    return this.$getOriginal("status") === PublishableStatus.ACTIVE;
  }

  $isDraft(): boolean {
    return this.$getOriginal("status") === PublishableStatus.DRAFT;
  }

  /**
   * Duplicate this resource with optional modifications
   * Must be implemented by child classes
   */
  abstract $duplicate(data?: Record<string, any>): Promise<any>;
}

export default PublishableResource;
